import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import {
  pollResultsCleared,
  signalingPollCreated,
  signalingPollDeleted,
  signalingPollEnded,
  signalingPollLaunched,
  signalingPollResultsShared,
  signalingPollResultsSharingStopped,
  signalingPollsReceived,
  signalingPollTemplateCreated,
  signalingPollTemplateDeleted,
  signalingPollTemplatesReceived,
} from 'features/polls/actions';
import {
  quizzesContentCleared,
  signalingQuizCreated,
  signalingQuizDeleted,
  signalingQuizEnded,
  signalingQuizLaunched,
  signalingQuizTemplateCreated,
  signalingQuizTemplateDeleted,
  signalingQuizTemplatesReceived,
  signalingQuizzesReceived,
} from 'features/quizzes/actions';
import { isPoll } from 'features/polls/types';
import {
  sidePanelClosed,
  sidePanelOpened,
  sidePanelPinned,
  sidePanelUnpinned,
  pinnedPanelsReceived,
} from 'features/side-panels/sidePanelsSlice';
import type { ContentScope, PollsPanelState, ViewName } from 'features/side-panels/polls/types';
import type { RootState } from 'store/store';

export const initialState: PollsPanelState = {
  panelOpen: false,
  exportProcessing: false,
  unreadUpdates: [],
  contentScope: 'room',
  orderedList: [],
  orderedTemplatesList: [],
  activeEntityType: null,
  activeEntityId: null,
  activeSessionId: null,
  activeView: {
    name: 'list',
    data: {},
  },
};

export const pollsPanelSlice = createSlice({
  name: 'pollsPanel',
  initialState,
  reducers: {
    viewChanged: (state, action: PayloadAction<{ name: ViewName; data: any }>) => {
      state.activeView = action.payload;
    },
    returnedToList: (state) => {
      state.activeView = { name: 'list', data: {} };
    },
    contentScopeChanged: (state, action: PayloadAction<ContentScope>) => {
      state.contentScope = action.payload;
      state.activeView = { name: 'list', data: {} };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signalingPollCreated, (state, action) => {
        const insertIndex = state.activeEntityId ? 1 : 0;
        state.orderedList.splice(insertIndex, 0, {
          type: 'poll',
          id: action.payload.id,
        });
      })
      .addCase(signalingPollLaunched, (state, action) => {
        const poll = action.payload.data;

        // Check if poll was relaunched (session ID changed)
        const sessionChanged = state.activeSessionId !== poll.sessionId;

        state.activeEntityType = 'poll';
        state.activeEntityId = poll.id;
        state.activeSessionId = poll.sessionId;

        // Move launched poll to top of list
        state.orderedList = state.orderedList.filter(
          (item) => !(item.type === 'poll' && item.id === poll.id)
        );
        state.orderedList.unshift({
          type: 'poll',
          id: poll.id,
        });

        // Reset to list view if poll was relaunched
        if (sessionChanged && state.activeSessionId !== null) {
          state.activeView = {
            name: 'list',
            data: {},
          };
        }

        if (!state.panelOpen) {
          state.unreadUpdates.push({
            type: 'poll',
            id: poll.id,
            updateType: 'launched',
          });
        }
      })
      .addCase(signalingPollEnded, (state, action) => {
        if (state.activeEntityId === action.payload.id && state.activeEntityType === 'poll') {
          state.activeEntityType = null;
          state.activeEntityId = null;
          state.activeSessionId = null;
        }

        if (!state.panelOpen) {
          state.unreadUpdates = state.unreadUpdates.filter(
            (update) => !(update.id === action.payload.id && update.updateType === 'launched')
          );
        }
      })
      .addCase(signalingPollDeleted, (state, action) => {
        state.orderedList = state.orderedList.filter(
          (item) => !(item.type === 'poll' && item.id === action.payload.id)
        );

        if (!state.panelOpen) {
          state.unreadUpdates = [];
        }
      })
      .addCase(signalingPollResultsShared, (state, action) => {
        if (isPoll(action.payload.data)) {
          if (!state.panelOpen) {
            state.unreadUpdates.push({
              type: 'poll',
              id: action.payload.data.id,
              updateType: 'shared',
            });
          }
        }
      })
      .addCase(signalingPollResultsSharingStopped, (state, action) => {
        if (!state.panelOpen) {
          state.unreadUpdates = state.unreadUpdates.filter(
            (update) => !(update.id === action.payload.id && update.updateType === 'shared')
          );
        }
      })
      .addCase(pollResultsCleared, (state, action) => {
        const { canManagePolls } = action.payload;

        state.activeEntityType = null;
        state.activeEntityId = null;
        state.activeSessionId = null;
        state.activeView = {
          name: 'list',
          data: {},
        };
        state.unreadUpdates = [];

        if (!canManagePolls) {
          state.orderedList = state.orderedList.filter((item) => item.type !== 'poll');
        }
      })
      .addCase(signalingPollsReceived, (state, action) => {
        const { polls } = action.payload;

        state.orderedList = polls.map((poll) => ({
          type: 'poll' as const,
          id: poll.id,
        }));

        const activePoll = polls.find((p) => p.active);
        if (activePoll) {
          // Check if poll was relaunched (session ID changed)
          const sessionChanged = state.activeSessionId !== activePoll.sessionId;

          state.activeEntityType = 'poll';
          state.activeEntityId = activePoll.id;
          state.activeSessionId = activePoll.sessionId;

          // Reset to list view if poll was relaunched
          if (sessionChanged && state.activeSessionId !== null) {
            state.activeView = {
              name: 'list',
              data: {},
            };
          }
        } else {
          state.activeEntityType = null;
          state.activeEntityId = null;
          state.activeSessionId = null;
        }
      })
      .addCase(signalingQuizCreated, (state, action) => {
        const insertIndex = state.activeEntityId ? 1 : 0;
        state.orderedList.splice(insertIndex, 0, {
          type: 'quiz',
          id: action.payload.id,
        });
      })
      .addCase(signalingQuizLaunched, (state, action) => {
        const quiz = action.payload.data;

        // @TODO reconnect
        // Check if quiz was relaunched (session ID changed)
        // const sessionChanged = state.activeSessionId !== quiz.sessionId;
        const sessionChanged = true;

        state.activeEntityType = 'quiz';
        state.activeEntityId = quiz.id;
        // state.activeSessionId = quiz.sessionId;

        // Move launched quiz to top of list
        state.orderedList = state.orderedList.filter(
          (item) => !(item.type === 'quiz' && item.id === quiz.id)
        );
        state.orderedList.unshift({
          type: 'quiz',
          id: quiz.id,
        });

        // Reset to list view if quiz was relaunched
        if (sessionChanged && state.activeSessionId !== null) {
          state.activeView = {
            name: 'list',
            data: {},
          };
        }

        if (!state.panelOpen) {
          state.unreadUpdates.push({
            type: 'quiz',
            id: quiz.id,
            updateType: 'launched',
          });
        }
      })
      .addCase(signalingQuizEnded, (state, action) => {
        const { id } = action.payload.data;
        if (state.activeEntityId === id && state.activeEntityType === 'quiz') {
          state.activeEntityType = null;
          state.activeEntityId = null;
          state.activeSessionId = null;
        }

        if (!state.panelOpen) {
          state.unreadUpdates = state.unreadUpdates.filter(
            (update) => !(update.id === id && update.updateType === 'launched')
          );
        }
      })
      .addCase(signalingQuizDeleted, (state, action) => {
        const deletedQuizId = action.payload.id;

        state.orderedList = state.orderedList.filter(
          (item) => !(item.type === 'quiz' && item.id === deletedQuizId)
        );

        if (!state.panelOpen) {
          state.unreadUpdates = [];
        }
      })
      .addCase(quizzesContentCleared, (state, action) => {
        const { canManagePolls } = action.payload;

        state.activeEntityType = null;
        state.activeEntityId = null;
        state.activeSessionId = null;
        state.activeView = {
          name: 'list',
          data: {},
        };
        state.unreadUpdates = [];

        if (!canManagePolls) {
          state.orderedList = state.orderedList.filter((item) => item.type !== 'quiz');
        }
      })
      .addCase(signalingQuizzesReceived, (state, action) => {
        const { quizzes } = action.payload;

        quizzes.forEach((quiz) => {
          state.orderedList.push({
            type: 'quiz' as const,
            id: quiz.id,
          });
        });

        const activeQuiz = quizzes.find((q) => q.status === 'launched');
        if (activeQuiz) {
          // Check if quiz was relaunched (session ID changed)
          const sessionChanged = state.activeSessionId !== activeQuiz.sessionId;

          state.activeEntityType = 'quiz';
          state.activeEntityId = activeQuiz.id;
          state.activeSessionId = activeQuiz.sessionId;

          // Move active quiz to top
          state.orderedList = state.orderedList.filter(
            (item) => !(item.type === 'quiz' && item.id === activeQuiz.id)
          );
          state.orderedList.unshift({
            type: 'quiz',
            id: activeQuiz.id,
          });

          // Reset to list view if quiz was relaunched
          if (sessionChanged && state.activeSessionId !== null) {
            state.activeView = {
              name: 'list',
              data: {},
            };
          }
        }
      })
      .addCase(sidePanelOpened, (state, action) => {
        if (action.payload === 'polls') {
          state.panelOpen = true;
          state.unreadUpdates = [];
        } else {
          state.panelOpen = false;
        }
      })
      .addCase(sidePanelClosed, (state) => {
        state.panelOpen = false;
      })
      .addCase(sidePanelPinned, (state, action) => {
        if (action.payload === 'polls') {
          state.panelOpen = true;
          state.unreadUpdates = [];
        }
      })
      .addCase(sidePanelUnpinned, (state, action) => {
        if (action.payload === 'polls') {
          state.panelOpen = false;
        }
      })
      .addCase(pinnedPanelsReceived, (state, action) => {
        if (action.payload.panels.includes('polls')) {
          state.panelOpen = true;
          state.unreadUpdates = [];
        }
      })
      .addCase(signalingPollTemplateCreated, (state, action) => {
        state.orderedTemplatesList.unshift({
          type: 'poll',
          id: action.payload.id,
        });
      })
      .addCase(signalingPollTemplateDeleted, (state, action) => {
        state.orderedTemplatesList = state.orderedTemplatesList.filter(
          (item) => !(item.type === 'poll' && item.id === action.payload.id)
        );
      })
      .addCase(signalingPollTemplatesReceived, (state, action) => {
        const pollTemplateItems = action.payload.templates.map((template) => ({
          type: 'poll' as const,
          id: template.id,
        }));

        state.orderedTemplatesList = pollTemplateItems;
      })
      .addCase(signalingQuizTemplateCreated, (state, action) => {
        state.orderedTemplatesList.unshift({
          type: 'quiz',
          id: action.payload.id,
        });
      })
      .addCase(signalingQuizTemplateDeleted, (state, action) => {
        state.orderedTemplatesList = state.orderedTemplatesList.filter(
          (item) => !(item.type === 'quiz' && item.id === action.payload.id)
        );
      })
      .addCase(signalingQuizTemplatesReceived, (state, action) => {
        const quizTemplateItems = action.payload.templates.map((template) => ({
          type: 'quiz' as const,
          id: template.id,
        }));

        state.orderedTemplatesList.push(...quizTemplateItems);
      });
  },
});

export const { viewChanged, returnedToList, contentScopeChanged } = pollsPanelSlice.actions;

export default pollsPanelSlice.reducer;

export const selectActiveView = (state: RootState) => state.sidePanels.polls.activeView;

export const selectActiveEntity = createSelector(
  [
    (state: RootState) => state.sidePanels.polls.activeEntityType,
    (state: RootState) => state.sidePanels.polls.activeEntityId,
    (state: RootState) => state.sidePanels.polls.activeSessionId,
  ],
  (activeEntityType, activeEntityId, activeSessionId) => ({
    type: activeEntityType,
    id: activeEntityId,
    sessionId: activeSessionId,
  })
);

export const selectUnreadPollUpdates = (state: RootState) => state.sidePanels.polls.unreadUpdates;

export const selectContentScope = (state: RootState) => state.sidePanels.polls.contentScope;

export const selectOrderedTemplatesList = (state: RootState) =>
  state.sidePanels.polls.orderedTemplatesList;

export const selectPollTemplatesCount = createSelector(
  [selectOrderedTemplatesList],
  (list) => list.filter((item) => item.type === 'poll').length
);

export const selectQuizTemplatesCount = createSelector(
  [selectOrderedTemplatesList],
  (list) => list.filter((item) => item.type === 'quiz').length
);

export const selectOrderedList = (state: RootState) => state.sidePanels.polls.orderedList;
